import { ContentCopy as ContentCopyIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { toPng } from 'html-to-image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CloseButton } from '@/components/ds';
import {
  ConsolidatedRfqDocument,
  RenderMode,
  RfqPage,
  THEMES,
} from '@/components/home/rfqs/RfqDocument';
import { RequestForQuotation } from '@/models/RequestForQuotation';
import { getSharedRFQ, SharedRFQPayload } from '@/services/api/rfqs';

const MOCK_PROPERTY: SharedRFQPayload['property'] = {
  address: '123 Main Street',
  city: 'Los Gatos',
  state: 'CA',
  zipCode: '95033',
};

const MOCK_ISSUER: SharedRFQPayload['issuer'] = {
  name: 'Jessie Casner',
  email: 'jcasner@gmail.com',
  phoneNumber: '(858) 555-1212',
};

const baseRfqTimestamp = new Date('2024-12-06T12:00:00Z').getTime();

const buildMockRfq = (
  id: string,
  projectTitle: string,
  objective: string,
  scope: string[],
  start: string,
  completion: string,
): RequestForQuotation => ({
  id,
  propertyId: 'mock-property',
  propertyAddress: '123 Main Street, Los Gatos, CA 95033',
  issueId: `mock-issue-${id}`,
  projectTitle,
  objective,
  scopeOfWork: scope,
  clientContact: {
    name: 'Homeowner',
    phoneNumber: '[Redacted]',
    email: '[Redacted]',
  },
  timeline: {
    desiredStartDate: start,
    completionDate: completion,
  },
  nextSteps:
    'Contractors are welcome to review prior inspection reports, perform their own onsite assessment, or order to provide accurate estimates and timelines.',
  submissionRequirements: {
    dueInDays: 7,
    method: 'Email proposal to jcasner@gmail.com',
    include: [
      'Cost breakdown for materials and labor',
      'Proposed timeline for start and completion',
      'Proof of licensing and insurance (Bond, WC and GL)',
      'Warranty information for materials and workmanship',
    ],
  },
  createdAt: baseRfqTimestamp,
  updatedAt: baseRfqTimestamp,
});

const MOCK_SINGLE: SharedRFQPayload = {
  rfqs: [
    buildMockRfq(
      'mock-rfq-1',
      'Installation of Handrail on Rear Stairs',
      'Install a compliant handrail on rear stairs to enhance safety and meet building code requirements.',
      [
        'Measure and evaluate stair dimensions',
        'Supply and install a durable, weather-resistant handrail',
        'Ensure compliance with local codes (34-38" height, 1 1/4-2" grip diameter)',
        'Securely anchor handrail and ensure proper alignment',
        'Remove debris and clean the worksite upon completion',
      ],
      'ASAP',
      'Within 1-2 days of project start',
    ),
  ],
  property: MOCK_PROPERTY,
  issuer: MOCK_ISSUER,
  expiresAt: baseRfqTimestamp / 1000 + 86400 * 90,
};

const MOCK_COMPILED: SharedRFQPayload = {
  rfqs: [
    MOCK_SINGLE.rfqs[0],
    buildMockRfq(
      'mock-rfq-2',
      'Replace Corroded Copper Main Line',
      'Remediate active plumbing leak from the corroded copper main and restore the affected subfloor and insulation beneath the laundry area.',
      [
        '**Main line:** Cut out ~12 ft of corroded 3/4" copper between meter and laundry wall. Replace with PEX-A and new shutoff valve.',
        '**Subfloor:** Remove ~24 sq ft of water-damaged 3/4" plywood; dry cavity to <15% moisture; replace with like material.',
        '**Insulation:** Replace wet fiberglass batts (R-19) in affected bay; add vapor barrier.',
        '**Finish:** Reinstall laundry hookups; pressure-test hot/cold supply at 60 psi for 24h.',
        '**Cleanup:** Haul off debris and leave work area broom-swept.',
      ],
      'March 25, 2025',
      'April 4, 2025',
    ),
    buildMockRfq(
      'mock-rfq-3',
      'Service Panel Upgrade to 200A',
      'Upgrade existing 100A panel to 200A service to support future EV charging and central HVAC capacity.',
      [
        'Pull permit and coordinate utility shutoff window with PG&E',
        'Replace meter base, main panel, and grounding/bonding to current code',
        'Re-terminate all existing branch circuits with proper labeling',
        'Install whole-home surge protective device',
        'Schedule and pass municipal final inspection',
      ],
      'April 8, 2025',
      'April 10, 2025',
    ),
  ],
  property: MOCK_PROPERTY,
  issuer: MOCK_ISSUER,
  expiresAt: baseRfqTimestamp / 1000 + 86400 * 90,
};

export const SharedRFQView = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const printableRef = useRef<HTMLDivElement>(null);
  const [renderMode, setRenderMode] = useState<RenderMode>('dark');
  const [isDownloading, setIsDownloading] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const mockParam = searchParams.get('mock');
  const mockData = useMemo<SharedRFQPayload | null>(() => {
    // sample RFQs for local design work only: this is a public page, so production never honours ?mock
    if (!import.meta.env.DEV) return null;
    if (mockParam === '1' || mockParam === 'single') return MOCK_SINGLE;
    if (mockParam === '2' || mockParam === 'compiled') return MOCK_COMPILED;
    return null;
  }, [mockParam]);

  const {
    data: fetched,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['shared-rfq', token],
    queryFn: () => getSharedRFQ(token as string),
    enabled: Boolean(token) && !mockData,
    retry: false,
  });
  const data = mockData ?? fetched;

  useEffect(() => {
    document.body.style.backgroundColor = '#262626';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  const theme = THEMES[renderMode];
  const errorMessage =
    error instanceof Error
      ? error.message
      : 'This share link is invalid, expired, or no longer available.';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      window.open(window.location.href, '_blank');
    }
  };

  const handleDownload = async () => {
    if (!printableRef.current || isDownloading) return;
    setIsDownloading(true);

    const pages = Array.from(printableRef.current.querySelectorAll<HTMLElement>('[data-rfq-page]'));
    const originalInnerPadding = pages.map(
      (p) => (p.firstElementChild as HTMLElement | null)?.style.padding ?? '',
    );

    const originalPrintableWidth = printableRef.current.style.width;

    try {
      setRenderMode('light');
      pages.forEach((p) => {
        const inner = p.firstElementChild as HTMLElement | null;
        if (inner) inner.style.padding = '40px';
      });
      printableRef.current.style.width = 'fit-content';

      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      const dataUrl = await toPng(printableRef.current, {
        pixelRatio: 2,
        backgroundColor: '#FFFFFF',
        cacheBust: true,
      });

      const safeAddress = (data?.property.address ?? 'document').replace(/[^a-zA-Z0-9 -]/g, '');
      const filename =
        data && data.rfqs.length > 1
          ? `RFQ Compiled - ${data.rfqs.length} items - ${safeAddress}.png`
          : `RFQ - ${safeAddress}.png`;

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download RFQ PNG', err);
    } finally {
      pages.forEach((p, i) => {
        const inner = p.firstElementChild as HTMLElement | null;
        if (inner) inner.style.padding = originalInnerPadding[i];
      });
      if (printableRef.current) printableRef.current.style.width = originalPrintableWidth;
      setRenderMode('dark');
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
        }}>
        <CircularProgress sx={{ color: '#C0FF02' }} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          color: 'var(--tx-text)',
          fontFamily: "'Manrope', sans-serif",
          p: 4,
          textAlign: 'center',
        }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 600, mb: 2 }}>RFQ unavailable</Typography>
          <Typography sx={{ fontSize: 14, color: 'var(--tx-muted)' }}>{errorMessage}</Typography>
        </Box>
      </Box>
    );
  }

  // RFQ dialog CTA spec: 10px radius, 14/500, tonal fill (no outline).
  const ctaBar =
    renderMode === 'dark' ? (
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 72,
          display: 'flex',
          gap: 1.5,
          zIndex: 10,
          '@media print': { display: 'none' },
        }}
        data-html2canvas-ignore="true">
        <Button
          onClick={handleCopyLink}
          variant="outlined"
          startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
          sx={{
            borderRadius: '10px',
            border: 0,
            backgroundColor: 'var(--tx-fill)',
            color: 'var(--tx-lime-ink)',
            fontSize: 14,
            fontWeight: 500,
            '&:hover': { border: 0, backgroundColor: 'var(--tx-fill-2)' },
          }}>
          {copyState === 'copied' ? 'Link copied' : 'Copy link'}
        </Button>
        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          variant="outlined"
          startIcon={
            isDownloading ? (
              <CircularProgress size={14} sx={{ color: 'inherit' }} />
            ) : (
              <DownloadIcon sx={{ fontSize: '16px !important' }} />
            )
          }
          sx={{
            borderRadius: '10px',
            border: 0,
            backgroundColor: 'var(--tx-fill)',
            color: 'var(--tx-muted)',
            fontSize: 14,
            fontWeight: 500,
            '&:hover': { border: 0, backgroundColor: 'var(--tx-fill-2)' },
            '&.Mui-disabled': { border: 0 },
          }}>
          {isDownloading ? 'Preparing…' : 'Download'}
        </Button>
      </Box>
    ) : null;

  // Breathing room below the fixed CTA row before the doc. Mobile gets
  // an extra 30px because the buttons read tighter on narrow viewports.
  const docTopOffset =
    renderMode === 'dark' && !isDownloading ? (isMobile ? '70px' : '40px') : 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.bg,
        color: theme.text,
        fontFamily: "'Manrope', sans-serif",
        paddingTop: docTopOffset,
      }}>
      {ctaBar}

      {/* Floating close (returns to dashboard if authed, otherwise no-op) */}
      <Box component="span" data-html2canvas-ignore="true" sx={{ '@media print': { display: 'none' } }}>
        <CloseButton
          onClose={() => navigate('/')}
          label="Close"
          sx={{
            position: 'fixed',
            top: 18,
            right: 18,
            zIndex: 10,
            color: theme.textMuted,
            '&:hover': { color: theme.text },
          }}
        />
      </Box>

      {data.rfqs.length > 1 ? (
        <Box ref={printableRef}>
          <Box data-rfq-page="true">
            <ConsolidatedRfqDocument
              rfqs={data.rfqs}
              property={data.property}
              issuer={data.issuer}
              theme={theme}
              mode={renderMode}
            />
          </Box>
        </Box>
      ) : (
        <Box ref={printableRef}>
          {data.rfqs.map((rfq) => (
            <Box key={rfq.id} data-rfq-page="true">
              <RfqPage
                rfq={rfq}
                property={data.property}
                issuer={data.issuer}
                theme={theme}
                mode={renderMode}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
